import { ApiError, apiRequest } from '@/lib/queryClient';
import type {
  ImportedTemplate,
  ShareLink,
  ShareTemplateEmailResult,
  ShareTemplateEmailVariables,
  SharedTemplatePreview,
} from '@/types/sharedTemplate';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const createShareLink = async (templateId: number): Promise<ShareLink> =>
  (await apiRequest('POST', `/api/templates/${templateId}/share-link`)).json();

// The BFF mints its own link, so this works without a prior createShareLink call.
const shareTemplateByEmail = async ({
  templateId,
  email,
}: ShareTemplateEmailVariables): Promise<ShareTemplateEmailResult> =>
  (await apiRequest('POST', `/api/templates/${templateId}/share-email`, { email })).json();

const fetchSharedTemplate = async (token: string): Promise<SharedTemplatePreview> =>
  (await apiRequest('POST', '/api/templates/shared/preview', { token })).json();

const importSharedTemplate = async (token: string): Promise<ImportedTemplate> =>
  (await apiRequest('POST', '/api/templates/shared/import', { token })).json();

export const useCreateShareLink = () => useMutation({ mutationFn: createShareLink });

export const useShareTemplateByEmail = () => useMutation({ mutationFn: shareTemplateByEmail });

export const useSharedTemplate = (token: string) =>
  useQuery({
    queryKey: ['templates', 'shared', token],
    queryFn: () => fetchSharedTemplate(token),
    // Global default is staleTime: Infinity; a reopened link must show current content.
    staleTime: 0,
    refetchOnMount: 'always',
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 2,
  });

export const useImportSharedTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: importSharedTemplate,
    onSuccess: () => {
      // Not awaited: the caller navigates away; the list refetches on mount.
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};
